import {
  filter,
  mergeMap,
  catchError,
  map,
  tap,
  last,
  exhaustMap,
} from "rxjs/operators";
import { of, from } from "rxjs";
import { Epic, ofType } from "redux-observable";
import { RootState } from "../../store";
import * as authSelectors from "../auth/authSelectors";
import * as actions from "./shopActions";
import { ServiceContainer } from "../../serviceContainer";
import { AnyAction } from "redux";
import { AjaxError } from "rxjs/ajax";
import { extractAjaxError } from "../../error-handler";

export const shopListEpic: Epic<any, any, RootState, ServiceContainer> = (
  action$,
  state$,
  services
) =>
  action$.pipe(
    ofType(actions.GET_SHOP_LIST),
    exhaustMap((action: actions.GetShopListAction) => {
      const state = state$.value;

      return services.apiShop
        .list(authSelectors.accessToken(state), action.organization)
        .pipe(
          map((response) =>
            actions.getShopListSuccessAction(action.organization, response.data)
          ),
          catchError(() => {
            services.toastr.error(
              "Fail",
              "There was error while fetching shop list"
            );
            return of(actions.getShopListFailAction(action.organization));
          })
        );
    })
  );

export const getShopEpic: Epic<any, any, RootState, ServiceContainer> = (
  action$,
  state$,
  services
) =>
  action$.pipe(
    filter((action) => action.type === actions.GET_SHOP),
    mergeMap((action: actions.GetShopAction) =>
      services.apiShop
        .get(
          authSelectors.accessToken(state$.value),
          action.organization,
          action.id
        )
        .pipe(
          map((response) =>
            actions.getShopSuccessAction(
              action.organization,
              action.id,
              response.data
            )
          ),
          catchError(() => {
            services.toastr.error(
              "Fail",
              "There was error while fetching shop"
            );

            return of(
              actions.getShopFailAction(action.organization, action.id)
            );
          })
        )
    )
  );

export const getShopTimeslotsEpic: Epic<
  any,
  any,
  RootState,
  ServiceContainer
> = (action$, state$, services) =>
  action$.pipe(
    filter((action) => action.type === actions.GET_SHOP_TIMESLOTS),
    mergeMap((action: actions.GetShopTimeslotsAction) =>
      services.apiShop
        .listTimeslots(
          authSelectors.accessToken(state$.value),
          action.organization,
          action.id
        )
        .pipe(
          map((response) =>
            actions.getShopTimeslotsSuccessAction(
              action.organization,
              action.id,
              response.timeslots
            )
          ),
          catchError(() => {
            services.toastr.error(
              "Fail",
              "There was error while fetching shop"
            );

            return of(
              actions.getShopFailAction(action.organization, action.id)
            );
          })
        )
    )
  );

export const updateShopTimeslotEpic: Epic<
  any,
  any,
  RootState,
  ServiceContainer
> = (action$, state$, services) =>
  action$.pipe(
    filter((action) => action.type === actions.UPDATE_SHOP_TIMESLOT),
    mergeMap((action: actions.UpdateShopTimeslotAction) =>
      services.apiShop
        .updateTimeslot(
          authSelectors.accessToken(state$.value),
          action.organization,
          action.shopId,
          action.template
        )
        .pipe(
          map((response) =>
            actions.getShopTimeslotUpdateSuccessAction(
              action.organization,
              action.shopId,
              action.template.id,
              response?.timeslots
            )
          ),
          catchError(() => {
            services.toastr.error(
              "Fail",
              "There was error while fetching shop"
            );

            return of(
              actions.getShopFailAction(action.organization, action.shopId)
            );
          })
        )
    )
  );

export const upsertShopEpic: Epic<any, any, RootState, ServiceContainer> = (
  action$,
  state$,
  services
) =>
  action$.pipe(
    filter((action) => action.type === actions.UPSERT_SHOP),
    mergeMap((action: actions.UpsertShopAction) => {
      const token = authSelectors.accessToken(state$.value);

      if (action.id) {
        return services.apiShop
          .update(token, action.organization, action.id, action.data)
          .pipe(
            map((response) =>
              actions.upsertShopSuccessAction(action.organization, action.id)
            ),
            tap(() => {
              services.toastr.success("Success", "Shop updated");
            }),
            catchError((e) => {
              const errorMessage = extractAjaxError({ error: e });
              services.toastr.error("Error", errorMessage);
              return of(
                actions.upsertShopFailAction(
                  action.organization,
                  e.response.errors,
                  action.id
                )
              );
            })
          );
      } else {
        return services.apiShop
          .insert(token, action.organization, action.data)
          .pipe(
            last(),
            tap((x) => {
              services.toastr.success("Success", "Shop added");
            }),
            catchError((e) => {
              services.toastr.error(
                "Fail",
                "There was error while adding shop"
              );

              return of(
                actions.upsertShopFailAction(
                  action.organization,
                  e.response?.errors
                )
              );
            })
          );
      }
    })
  );

export const getProductCategoryListEpic: Epic<
  any,
  any,
  RootState,
  ServiceContainer
> = (action$, state$, services) =>
  action$.pipe(
    filter((action) => action.type === actions.GET_PRODUCT_CATEGORY_LIST),
    mergeMap((action: actions.GetProductCategoryListAction) =>
      services.apiShop
        .listProductCategories(
          authSelectors.accessToken(state$.value),
          action.organization
        )
        .pipe(
          map((response) =>
            actions.getProductCategoryListSuccessAction(
              action.organization,
              response.data
            )
          ),
          catchError(() => {
            services.toastr.error(
              "Fail",
              "There was error while fetching product category list"
            );

            return of(
              actions.getProductCategoryListFailAction(action.organization)
            );
          })
        )
    )
  );

export const getProductCategoryEpic: Epic<
  any,
  any,
  RootState,
  ServiceContainer
> = (action$, state$, services) =>
  action$.pipe(
    filter((action) => action.type === actions.GET_PRODUCT_CATEGORY),
    mergeMap((action: actions.GetProductCategoryAction) =>
      services.apiShop
        .getProductCategory(
          authSelectors.accessToken(state$.value),
          action.organization,
          action.id
        )
        .pipe(
          map((response) =>
            actions.getProductCategorySuccessAction(
              action.organization,
              action.id,
              response.data
            )
          ),
          catchError(() => {
            services.toastr.error(
              "Fail",
              "There was error while fetching product"
            );

            return of(
              actions.getProductCategoryFailAction(
                action.organization,
                action.id
              )
            );
          })
        )
    )
  );

export const upsertProductCategoryEpic: Epic<
  any,
  any,
  RootState,
  ServiceContainer
> = (action$, state$, services) =>
  action$.pipe(
    filter((action) => action.type === actions.UPSERT_PRODUCT_CATEGORY),
    mergeMap((action: actions.UpsertProductCategoryAction) => {
      const token = authSelectors.accessToken(state$.value);

      if (action.id) {
        return services.apiShop
          .updateProductCategory(
            token,
            action.organization,
            action.id,
            action.data
          )
          .pipe(
            map((response) =>
              actions.upsertProductCategorySuccessAction(
                action.organization,
                action.id
              )
            ),
            tap(() => {
              services.toastr.success("Success", "Product updated");
            }),
            catchError((e) => {
              services.toastr.error(
                "Fail",
                "There was error while updating shop"
              );

              return of(
                actions.upsertProductFailAction(
                  action.organization,
                  e.response.errors,
                  action.id
                )
              );
            })
          );
      } else {
        return services.apiShop
          .insertProductCategory(token, action.organization, action.data)
          .pipe(
            map((response) =>
              actions.upsertProductCategorySuccessAction(action.organization)
            ),
            tap(() => {
              services.toastr.success("Success", "Product category added");
            }),
            catchError((e) => {
              services.toastr.error(
                "Fail",
                "There was error while adding product"
              );

              return of(
                actions.upsertProductCategoryFailAction(
                  action.organization,
                  e.response.errors
                )
              );
            })
          );
      }
    })
  );

export const updateProductCategoryOrderEpic: Epic<
  any,
  any,
  RootState,
  ServiceContainer
> = (action$, state$, services) =>
  action$.pipe(
    filter((action) => action.type === actions.UPDATE_PRODUCT_CATEGORY_ORDER),
    mergeMap((action: actions.UpdateProductCategoryOrderAction) => {
      const token = authSelectors.accessToken(state$.value);

      return services.apiShop
        .updateProductCategoryOrder(token, action.organization, action.data)
        .pipe(
          map((response) =>
            actions.updateProductCategoryOrderSuccessAction(action.organization)
          ),
          mergeMap(() =>
            of(actions.getProductCategoryListAction(action.organization))
          ),
          tap(() => {
            services.toastr.success(
              "Success",
              "Product category order updated"
            );
          }),
          catchError((e) => {
            services.toastr.error(
              "Fail",
              "There was error while updating product category order"
            );

            return of(
              actions.updateProductCategoryOrderFailAction(action.organization)
            );
          })
        );
    })
  );

export const upsertHomepageEpic: Epic<any, any, RootState, ServiceContainer> = (
  action$,
  state$,
  services
) =>
  action$.pipe(
    filter((action) => action.type === actions.UPSERT_HOMEPAGE),
    mergeMap((action: actions.UpsertHomepageAction) => {
      const promise = async (): Promise<AnyAction[]> => {
        try {
          let r = [];

          const token = authSelectors.accessToken(state$.value);
          let homepageId = action.id;

          if (!!action.id) {
            await services.apiShop
              .updateHomepage(
                token,
                action.organization,
                action.id,
                action.data
              )
              .toPromise();

            services.toastr.success("Success", "Homepage updated");
          } else {
            const insertHomepageResponse = await services.apiShop
              .insertHomepage(token, action.organization, action.data)
              .toPromise();

            homepageId = insertHomepageResponse.id;

            services.toastr.success("Success", "Homepage added");
            r.push(
              actions.setIsVisibleNewHomepageConfig(action.organization, false)
            );
            r.push(actions.getHomepageListAction(action.organization));
          }

          r.push(actions.upsertHomepageSuccessAction(action.organization));

          if (action.image) {
            if (!homepageId) {
              throw new Error("No homepage id for file upload");
            }

            await services.apiUpload
              .uploadHomepageImage(
                token,
                action.organization,
                action.image,
                homepageId
              )
              .pipe(
                catchError(() => {
                  services.toastr.error(
                    "Error",
                    "Could not upload file for homepage"
                  );
                  return of();
                })
              )
              .toPromise();
          }

          return r;
        } catch (e: any) {
          return [
            actions.upsertProductFailAction(
              action.organization,
              e.response.errors
            ),
          ];
        }
      };

      return from(promise()).pipe(mergeMap(from));
    })
  );

export const homepageList: Epic<any, any, RootState, ServiceContainer> = (
  action$,
  state$,
  services
) =>
  action$.pipe(
    ofType(actions.GET_HOMEPAGE_LIST),
    exhaustMap((action: actions.GetHomepageListAction) => {
      const state = state$.value;

      return services.apiShop
        .listHomepages(authSelectors.accessToken(state), action.organization)
        .pipe(
          map((response) =>
            actions.getHomepageListSuccessAction(
              action.organization,
              response.data
            )
          ),
          catchError(() => {
            services.toastr.error(
              "Fail",
              "There was error while fetching homepage list"
            );
            return of(actions.getHomepageListFailAction(action.organization));
          })
        );
    })
  );

export const deleteHomepage: Epic<any, any, RootState, ServiceContainer> = (
  action$,
  state$,
  services
) =>
  action$.pipe(
    ofType(actions.DELETE_HOMEPAGE),
    mergeMap((action: actions.DeleteHomepageAction) => {
      const promise = async (): Promise<AnyAction[]> => {
        const r = [];
        const state = state$.value;

        try {
          await services.apiShop
            .deleteHomepage(
              authSelectors.accessToken(state),
              action.organization,
              action.id
            )
            .toPromise();

          r.push(
            actions.deleteHomepageSuccessAction(action.organization, action.id)
          );
          r.push(actions.getHomepageListAction(action.organization));
          services.toastr.success("Success", "Homepage config deleted");
        } catch (e) {
          r.push(
            actions.deleteHomepageFailAction(action.organization, action.id)
          );
          services.toastr.error(
            "Fail",
            "There was error while deleting homepage config"
          );
        }

        return r;
      };

      return from(promise()).pipe(mergeMap(from));
    })
  );

export const listUnassignedNewsEpic: Epic<
  any,
  any,
  RootState,
  ServiceContainer
> = (action$, state$, services) =>
  action$.pipe(
    filter((action) => action.type === actions.GET_UNASSIGNED_NEWS_LIST),
    mergeMap((action: actions.GetUnassignedNewsListAction) =>
      services.apiShop
        .listNewsUnassigned(
          authSelectors.accessToken(state$.value),
          action.organization
        )
        .pipe(
          map((response) =>
            actions.getUnassignedNewsListSuccessAction(
              action.organization,
              response.data
            )
          ),
          catchError(() => {
            services.toastr.error(
              "Fail",
              "There was error while fetching unaasigned news list"
            );

            return of(
              actions.getUnassignedNewsListFailAction(action.organization)
            );
          })
        )
    )
  );

export const getOrderEpic: Epic<any, any, RootState, ServiceContainer> = (
  action$,
  state$,
  services
) =>
  action$.pipe(
    filter((action) => action.type === actions.GET_ORDER),
    mergeMap((action: actions.GetOrderAction) =>
      services.apiShop
        .getOrder(
          authSelectors.accessToken(state$.value),
          action.organization,
          action.id
        )
        .pipe(
          map((response) =>
            actions.getOrderSuccessAction(
              action.organization,
              action.id,
              response.data
            )
          ),
          catchError(() => {
            services.toastr.error(
              "Fail",
              "There was error while fetching order"
            );

            return of(
              actions.getOrderFailAction(action.organization, action.id)
            );
          })
        )
    )
  );

export const refundOrderEpic: Epic<any, any, RootState, ServiceContainer> = (
  action$,
  state$,
  services
) =>
  action$.pipe(
    filter((action) => action.type === actions.REFUND_ORDER),
    mergeMap((action: actions.RefundOrderAction) =>
      services.apiShop
        .refundOrder(
          authSelectors.accessToken(state$.value),
          action.organization,
          action.id,
          action.amount
        )
        .pipe(
          mergeMap(() =>
            services.apiShop.getOrder(
              authSelectors.accessToken(state$.value),
              action.organization,
              action.id
            )
          ),
          map((response) => {
            services.toastr.success("Success", "Order refunded");
            return actions.getOrderSuccessAction(
              action.organization,
              action.id,
              response.data
            );
          }),
          catchError((e: AjaxError) => {
            if (
              e.xhr.status === 400 &&
              e.xhr.response.errors &&
              e.xhr.response.errors.length
            ) {
              services.toastr.error("Fail", e.xhr.response.errors[0].detail);
            } else {
              services.toastr.error(
                "Fail",
                "There was an error while refunding the order. Did you know that MobilePay orders can only be refunded if other payments has been made on the same day?"
              );
            }
            return of(
              actions.getOrderFailAction(action.organization, action.id)
            );
          })
        )
    )
  );

export const listOrdersEpic: Epic<any, any, RootState, ServiceContainer> = (
  action$,
  state$,
  services
) =>
  action$.pipe(
    filter((action) => action.type === actions.GET_ORDER_LIST),
    mergeMap((action: actions.GetOrderListAction) =>
      services.apiShop
        .listOrders(
          authSelectors.accessToken(state$.value),
          action.organization,
          action.filters,
          action.orderBy,
          action.page
        )
        .pipe(
          map((response) =>
            actions.getOrderListSuccessAction(
              action.organization,
              action.filters,
              response.data
            )
          ),
          catchError(() => {
            services.toastr.error(
              "Fail",
              "There was error while fetching orders. Please try selecting a different shop."
            );

            return of(
              actions.getOrderListFailAction(
                action.organization,
                action.filters
              )
            );
          })
        )
    )
  );
